import { HoldingSession } from "../../api/types"
import { OmniaConfig } from "../../config"
import { OmniaModuleId } from "../../generated/enums"
import { isAdmin } from "../../session/utils"
import { IOmniaShell } from "../../shell"
import { str } from "../../strings"
import { IOmniaSession } from "../../types"
import { RoleId } from "../../user"
import { defineDirective } from "../../utils/angular"
import { groupBy, selectMany } from "../../utils/array"
import { MenuItem } from "./menuItem"

const showAllEntriesMinimumHeight = 950

/**
 * Implements Omnia's main menu, displayed on the left side of the screen
 */
/** @internal */
export class MenuCtrl {
    static create() {
        return defineDirective(MenuCtrl, "shared/main/menu/menu.html")
    }

    _dispose: Function = () => {}

    allEntries: MenuItem[]
    nestedEntries: MenuItem[]

    menu: MenuItem[]
    selectedMenu: MenuItem

    /** Maps OmniaModuleId to an array of (other) sessions currently using this module */
    private _modulesInUse: Map<OmniaModuleId, HoldingSession[]> = new Map()

    public popover: { [key: string]: boolean } = {}

    static $inject = ["$scope", "$route", "$location", "$window", "OmniaShell"]

    constructor(
        private readonly $scope: ng.IScope,
        private readonly $route,
        private readonly $location: ng.ILocationService,
        private readonly $window: ng.IWindowService,
        private shell: IOmniaShell
    ) {}

    $onInit() {
        const { shell, $scope } = this
        const session = shell.getSession()

        this.setupMenu()

        $scope.$on("$routeChangeSuccess", () => this.selectMenuFromCurrentRoute())
        $scope.$on("omnia.sessionsChanged", (_, sessions) => this.refreshSessions(sessions))

        this.selectMenuFromCurrentRoute()
        this.refreshSessions(session.holdingSessions.active)
    }

    $onDispose() {
        this._dispose()
    }

    setupMenu() {
        this._dispose?.()

        const { shell } = this
        const session = shell.getSession()

        this.menu = this.nestedEntries = createMenu(session, shell.config)
        this.allEntries = expandNestedEntries(this.nestedEntries)
    }

    setupResizeHandler() {
        const { $window, $scope } = this

        const handler = () => {
            const menu = $window.innerHeight < showAllEntriesMinimumHeight ? this.nestedEntries : this.allEntries

            if (menu != this.menu) {
                this.menu = menu
                $scope.$applyAsync()
            }
        }

        $window.addEventListener("resize", handler)

        this._dispose = () => $window.removeEventListener("resize", handler)

        handler()
    }

    get disableMenu(): boolean {
        return this.shell.disableMenu
    }

    closePopover() {
        for (const key in this.popover) {
            this.popover[key] = false
        }
    }

    showPopover(menu: MenuItem) {
        this.closePopover()
        this.popover[menu.popupId] = true
    }

    selectMenu(menu: MenuItem) {
        if (menu.disabled) {
            return
        }

        if (this.disableMenu) {
            return
        }

        this.closePopover()

        this.selectedMenu = menu

        this.$location.path(menu.targetUrl)
    }

    selectMenuFromCurrentRoute() {
        const path = this.$route.current.$$route.originalPath

        const menu = this.findMenuItem((i) => i.targetUrl && path.indexOf(i.targetUrl + "/") == 0)

        if (menu && this.selectedMenu != menu) {
            this.selectMenu(menu)
        }
    }

    findMenuItem(predicate: (i: MenuItem) => boolean): MenuItem {
        const { allEntries } = this

        if (!allEntries) {
            return
        }

        for (const m of allEntries) {
            if (predicate(m)) return m
            if (m.items) {
                const s = m.items.find(predicate)
                if (s) return s
            }
        }

        return null
    }

    shouldShowUsersTag(menu: MenuItem) {
        return menu && menu.moduleId && this._modulesInUse.has(menu.moduleId)
    }

    getUsersTagContent(menu: MenuItem) {
        return menu && menu.moduleId && this._modulesInUse.get(menu.moduleId)?.length
    }

    isSelected(menu: MenuItem) {
        return menu == this.selectedMenu || menu?.items?.some((i) => i == this.selectedMenu)
    }

    /**
     * This is called upon initialization, and each time the sessions connected to the same holding change,
     * to refresh the _moduleInUsed member
     * */
    refreshSessions(sessions: HoldingSession[]) {
        const modules = sessions.filter((s) => !s.CurrentSession).filter((s) => s?.ClientState?.moduleId)
        const groups = groupBy(modules, (s) => s.ClientState.moduleId)
        this._modulesInUse = new Map(groups.map((g) => [g.key, g.values]))
    }
}

function createMenu(session: IOmniaSession, host: OmniaConfig): MenuItem[] {
    const { features } = host
    const {
        ui: { mainMenu },
    } = host
    const {
        fields,
        soil,
        mapLayers,
        exports,
        imports,
        manure,
        nutritionPlans,
        seedPlans,
        stocks,
        sprayPlans,
        reports,
    } = session.permissions

    const names = str.Modules

    const holding = [
        menu(OmniaModuleId.Dashboard, names.Dashboard, "holding-dashboard", "/dashboard"),
        menu(OmniaModuleId.Fields, names.Fields, "holding-fields", "/fields", fields.read),
        menu(OmniaModuleId.Layers, names.MapLayers, "datalayers", "/datalayers", mapLayers.read, {
            shortName: names.MapLayers,
        }),
        menu(OmniaModuleId.Soil, "Soil analysis", "soil-analysis", "/soil", soil.read, { shortName: "Soil" }),
        menu(OmniaModuleId.Stocks, names.Stocks, "stocks", "/stocks", stocks.read),
    ]

    /** omnia-i18n-ignore-strings */
    const plans = [
        menu(OmniaModuleId.Seeds, "Seeds", "reco-seeds", "/seeds", seedPlans.read && features.seeds),
        menu(
            OmniaModuleId.Nutrition,
            "Nutrition",
            "nutrition",
            "/nutrition",
            nutritionPlans.read && features.nutrition
        ),
        menu(OmniaModuleId.CropProtection, "Spray", "sprayer", "/spray", sprayPlans.read && features.sprayPlans),
        menu(
            OmniaModuleId.Prescriptions,
            "Prescriptions",
            "fas fa-prescription",
            "/prescriptions",
            features.prescriptionPlans
        ),
    ]

    /** omnia-i18n-ignore-strings */
    const mmp = [menu(OmniaModuleId.Manure, "Manure", "mmp", "/manure", manure.read && features.manure)]

    const data = new MenuItem(names.Data, "fas fa-exchange-alt")
    data.popupId = "data"

    data.items = [
        menu(OmniaModuleId.Imports, names.Import, "data-import", "/import", imports.enabled),
        menu(OmniaModuleId.Exports, names.Export, "data-export", "/export", exports.enabled),
    ]

    const reportsMenu = [menu(OmniaModuleId.Reports, names.Reports, "reports", "/reports", reports.enabled)]

    const reportsMenu2 = [
        menu(OmniaModuleId.Reports2, names.Reports2, "reports", "/reports-june-2025", false/*reports.enabled*/, { beta: true })
    ]

    return [
        ...holding,
        ...plans,
        ...mmp,
        ...getClimateModule(session, host),
        ...getDiaryModule(session, host),
        getBusinessPlanningModule(session),
        data,
        ...reportsMenu,
        ...reportsMenu2
    ].filter(shouldIncludeMenu)
}

function shouldIncludeMenu(m: MenuItem) {
    if (!m) {
        return false
    }

    if (m.disabled) {
        return false
    }

    if (m.items && m.items.length && m.items.every((i) => i.disabled)) {
        return false
    }

    return true
}

function getClimateModule(session: IOmniaSession, host: OmniaConfig) {
    if (!host.features.climate) {
        return []
    }

    const basic = session.currentUserRole == RoleId.Access
    const p = session.permissions.climate

    const enabled = p.read || basic

    return [menu(OmniaModuleId.Climate, "Climate", "climate", "/climate", enabled)]
}

function getDiaryModule(session: IOmniaSession, host: OmniaConfig) {
    const p = session.permissions
    const enabled = p.fieldDiary.read || p.taskManagement.read

    return [menu(OmniaModuleId.FieldDiary, "Field diary", "far fa-calendar", "/diary", enabled)]
}

function getBusinessPlanningModule(session: IOmniaSession) {
    const { yield: yieldModule, shed, production, planning } = session.permissions
    const admin = isAdmin(session)

    const enabled = yieldModule.read || shed.read || production.read || planning.read

    if (!enabled) {
        return
    }

    const bp = new MenuItem("Business", "far fa-chart-bar")
    bp.popupId = "bp"

    bp.items = [
        menu(OmniaModuleId.FieldPerformance, "Field performance", "yield", "/yield", yieldModule.read, {
            shortName: "Yield",
        }),
        menu(OmniaModuleId.Shed, "Shed", "fas fa-tractor", "/bp-shed", shed.read),
        menu(OmniaModuleId.CostOfProduction, "Production", "fas fa-pound-sign", "/bp-prod", production.read),
        menu(OmniaModuleId.Planning, "Planning", "far fa-calendar-alt", "/bp-plan", planning.read),
        admin
            ? menu(OmniaModuleId.FieldPerformance, "Performance", "fas fa-chart-line", "/bp-perf", yieldModule.read)
            : undefined,
    ].filter((m) => !!m)

    return bp
}

interface NewModuleOptions {
    shortName?: string
    beta?: boolean
}

function menu(
    id: OmniaModuleId,
    name: string,
    icon: string,
    url: string,
    enabled: boolean = true,
    options: NewModuleOptions = {}
) {
    const item = new MenuItem(name, icon, url, [], !enabled)
    item.moduleId = id
    item.shortName = options?.shortName
    item.beta = options?.beta
    return item
}

function expandNestedEntries(items: MenuItem[]) {
    return selectMany(items.map((i) => (i.popupId && i.items ? i.items.filter((i) => !i.disabled) : [i])))
}
